import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, of } from 'rxjs';
import { map, mergeMap, switchMap } from 'rxjs/operators';
import { firstAvailable } from '@zerops/zef/core';
import { UserEntity } from '@zerops/zerops/core/user-base';
import { integrationTokenDelegationActions } from './integration-token-delegation-base.state';
import { delegationTokenTag } from './integration-token-delegation-base.constant';
import { IntegrationTokenDelegationApi } from './integration-token-delegation.api';
import { IntegrationTokenDelegationEntity } from './integration-token-delegation.entity';

@Injectable()
export class IntegrationTokenDelegationBaseEffect {
  #actions$ = inject(Actions);
  #api = inject(IntegrationTokenDelegationApi);
  #entity = inject(IntegrationTokenDelegationEntity);
  #userEntity = inject(UserEntity);

  onLoadAllDelegations$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(integrationTokenDelegationActions.loadAllDelegations),
      switchMap((action) => this.#userEntity.activeClientId$.pipe(
        firstAvailable(),
        switchMap((clientId) =>
          this.#api.listAll$(clientId).pipe(
            map((response) => integrationTokenDelegationActions.loadAllDelegationsSuccess(response, action)),
            catchError((err) => of(integrationTokenDelegationActions.loadAllDelegationsFail(err, action)))
          )
        )
      ))
    )
  );

  onLoadAllDelegationsSuccess$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(integrationTokenDelegationActions.loadAllDelegationsSuccess),
      // listReset first — addIdsToCache only appends, so a plain reload would
      // never drop ids that no longer exist server-side. Reset = replace.
      mergeMap((action) => [
        this.#entity.listReset(),
        this.#entity.addToCache(action.data.list),
        this.#entity.addIdsToCache(
          action.data.list.map(({ id }) => id),
          { handleGlobally: true }
        )
      ])
    )
  );

  // Scoped load for a single token's delegations (edit dialog). Cached under a
  // per-token tag so it's independent of the client-wide list used for badges,
  // and so opening a token never pulls every delegation on the client.
  onLoadTokenDelegations$ = createEffect(() =>
    this.#actions$.pipe(
      ofType(integrationTokenDelegationActions.loadTokenDelegations),
      switchMap((action) => this.#userEntity.activeClientId$.pipe(
        firstAvailable(),
        switchMap((clientId) =>
          this.#api.listForToken$(clientId, action.data.tokenId).pipe(
            // listReset first so a reload replaces the per-token list rather than
            // appending — otherwise a deleted delegation lingers (addIdsToCache
            // only adds), and an empty response wouldn't clear it.
            mergeMap((response) => [
              integrationTokenDelegationActions.loadTokenDelegationsSuccess(response, action),
              this.#entity.listReset(delegationTokenTag(action.data.tokenId)),
              this.#entity.addToCache(response.list),
              this.#entity.addIdsToCache(
                response.list.map(({ id }) => id),
                { tag: delegationTokenTag(action.data.tokenId), handleGlobally: true }
              )
            ]),
            catchError((err) => of(integrationTokenDelegationActions.loadTokenDelegationsFail(err, action)))
          )
        )
      ))
    )
  );

  // Note: no client-wide refresh on create/delete — AddOne/DeleteOne already
  // patch the entity's global list (which the token-list badge reads), so the
  // badge updates without re-pulling every delegation. The edit dialog refreshes
  // its own per-token list separately (see the dialog feature).
}
